import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang, waLink, EASE } from "@/lib/site";
import { copy, ebooks } from "@/lib/content";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { EbookCover } from "./EbookCover";

const EbookCard = ({ book, i, lang, t }) => {
  return (
    <motion.article data-testid={`ebook-card-${book.slug}`}
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.12, ease: EASE }}
      className={`ebook-card group relative flex h-full flex-col rounded-[1.75rem] border p-3 ${book.bundle ? "border-amber-300/50 bg-[#141A2B]" : "border-amber-200/10 bg-[#0D1322]"}`}>
      <div className="spotlight relative overflow-hidden rounded-[1.4rem] py-12">
        <span className="absolute left-4 top-4 rounded-full border border-amber-200/25 bg-[#070A12]/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-200">
          {book.bundle ? t.bundleSave : book.tag[lang]}
        </span>
        <EbookCover book={book} />
      </div>
      <div className="flex flex-1 flex-col px-4 pb-4 pt-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">{book.bundle ? book.tag[lang] : t.format}</p>
        <h3 data-testid={`ebook-title-${book.slug}`} className="font-display mt-2 text-3xl leading-tight text-slate-50">{book.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-300">{book.sub[lang]}</p>
        <ul className="mt-5 space-y-2.5">
          {book.points[lang].map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm text-slate-300"><Check size={16} className="mt-0.5 shrink-0 text-amber-300" />{p}</li>
          ))}
        </ul>
        <div className="mt-auto flex items-center justify-between gap-4 border-t border-white/5 pt-6 mt-8">
          <span data-testid={`ebook-price-${book.slug}`} className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-200/80">{t.priceHidden}</span>
          <a data-testid={`buy-ebook-${book.slug}-button`} href={waLink(t.waBuy(book.title))} target="_blank" rel="noopener noreferrer"
            className="btn-gold group/btn inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold whitespace-nowrap">
            {t.buy}<ArrowUpRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

export const Ebooks = () => {
  const { lang } = useLang();
  const t = copy[lang];
  return (
    <section id="ebook" data-testid="ebooks-section" className="relative py-28 lg:py-40 bg-[#090D18] border-y border-amber-200/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7"><Eyebrow>{t.ebooksEyebrow}</Eyebrow><SectionTitle lines={t.ebooksTitle} testId="ebooks-title" /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-5"><p className="text-slate-300 text-base sm:text-lg leading-relaxed lg:pb-3">{t.ebooksSub}</p></Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ebooks.map((b, i) => <EbookCard key={b.slug} book={b} i={i} lang={lang} t={t} />)}
        </div>
      </div>
    </section>
  );
};
